import { create } from 'zustand';

import type { DomainAggregationBucket, OpenMetadataAggregations } from '@/shared/api/openmetadata';
import type { SortByOption } from '@/shared/types/options';

import type { FilterState } from '@/entities/discovery/domain/types';

import type { SearchResult } from './types';

type SearchState = {
  searchValue: string;
  query: string;
  sortBy: SortByOption;
  filters?: FilterState;
  aggregations: OpenMetadataAggregations | null;
  initialAggregations: OpenMetadataAggregations | null;
  domainAggregations: DomainAggregationBucket[] | null;
  filteredResults: SearchResult[];
  isInitialized: boolean;
  hasUrlParams: boolean;
  isInitialAggregationsLoaded: boolean;
  setQuery: (query: string) => void;
  setSearchValue: (searchValue: string) => void;
  setSortBy: (sortBy: SortByOption) => void;
  setFilters: (filters: FilterState) => void;
  setAggregations: (aggregations: OpenMetadataAggregations | null) => void;
  setInitialAggregations: (aggregations: OpenMetadataAggregations | null) => void;
  setDomainAggregations: (domains: DomainAggregationBucket[] | null) => void;
  setFilteredResults: (results: SearchResult[]) => void;
  setInitialized: (initialized: boolean) => void;
  setHasUrlParams: (hasParams: boolean) => void;
  setInitialAggregationsLoaded: (loaded: boolean) => void;
};

export const useSearchStore = create<SearchState>((set) => ({
  searchValue: '',
  query: '',
  sortBy: 'lastUpdated',
  filters: undefined,
  aggregations: null,
  initialAggregations: null,
  domainAggregations: null,
  filteredResults: [],
  isInitialized: false,
  hasUrlParams: false,
  isInitialAggregationsLoaded: false,
  setSearchValue: (searchValue) => set({ searchValue }),
  setQuery: (query) => set({ query }),
  setSortBy: (sortBy) => set({ sortBy }),
  setFilters: (filters) => set({ filters }),
  setAggregations: (aggregations) => set({ aggregations }),
  setInitialAggregations: (aggregations) =>
    set({ initialAggregations: aggregations, isInitialAggregationsLoaded: true }),
  setDomainAggregations: (domains) => set({ domainAggregations: domains }),
  setFilteredResults: (results) => set({ filteredResults: results }),
  setInitialized: (initialized) => set({ isInitialized: initialized }),
  setHasUrlParams: (hasParams) => set({ hasUrlParams: hasParams }),
  setInitialAggregationsLoaded: (loaded) => set({ isInitialAggregationsLoaded: loaded }),
}));
